import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('Home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update active section based on current route
  useEffect(() => {
    if (location.pathname === '/') {
      // We're on the main page, detect active section by scroll position
      const handleScrollDetection = () => {
        const sections = ['landing', 'projects', 'technologies', 'blogs', 'about', 'contact'];
        const scrollPosition = window.scrollY + 100;

        for (const sectionId of sections) {
          const element = document.getElementById(sectionId);
          if (element) {
            const { offsetTop, offsetHeight } = element;
            if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
              const sectionName = 
                sectionId === 'landing' ? 'Home' : 
                sectionId === 'about' ? 'About us' :
                sectionId === 'technologies' ? 'Our Services' :
                sectionId === 'projects' ? 'Project' :
                sectionId.charAt(0).toUpperCase() + sectionId.slice(1);
              setActiveSection(sectionName);
              break;
            }
          }
        }
      };

      window.addEventListener('scroll', handleScrollDetection);
      handleScrollDetection(); // Initial check
      return () => window.removeEventListener('scroll', handleScrollDetection);
    } else if (location.pathname.startsWith('/project/')) {
      // We're on a project detail page
      setActiveSection('Project');
    } else if (location.pathname.startsWith('/blog/')) {
      // We're on a blog detail page
      setActiveSection('Blogs');
    }
  }, [location.pathname]);

  const scrollToSection = (id) => {
    // Close mobile menu when navigating
    setIsMobileMenuOpen(false);
    
    // If we're not on the main page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          if (window.lenis) {
            window.lenis.scrollTo(element);
          } else {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }, 150);
    } else {
      // We're already on the main page, just scroll
      const element = document.getElementById(id);
      if (element) {
        if (window.lenis) {
          window.lenis.scrollTo(element);
        } else {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navItems = [
    { label: 'Home', id: 'landing' },
    { label: 'Project', id: 'projects' },
    { label: 'Our Services', id: 'technologies' },
    { label: 'Blogs', id: 'blogs' },
    { label: 'About us', id: 'about' },
    { label: 'Contact', id: 'contact' }
  ];

  return (
    <motion.nav
      className={`navbar ${scrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="nav-container">
        <motion.div
          className="nav-logo"
          whileHover={{ scale: 1.05 }}
          onClick={() => scrollToSection('landing')}
        >
          <img src="/images/Company_logo_new.png" alt="GlobeSoft Solutions Logo" className="logo-image" />
          GlobeSoft Solutions
        </motion.div>
        <div className="nav-links">
          {navItems.map((item, index) => (
            <motion.button
              key={item.label}
              className={`nav-link ${activeSection === item.label ? 'active' : ''}`}
              onClick={() => scrollToSection(item.id)}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {item.label}
            </motion.button>
          ))}
        </div>
        
        {/* Hamburger Menu Button */}
        <button 
          className={`hamburger-menu ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        
        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div 
              className="mobile-menu"
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              {navItems.map((item, index) => (
                <motion.button
                  key={item.label}
                  className={`mobile-nav-link ${activeSection === item.label ? 'active' : ''}`}
                  onClick={() => scrollToSection(item.id)}
                  initial={{ opacity: 0, x: -50, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -50, scale: 0.9 }}
                  transition={{ 
                    delay: index * 0.08,
                    duration: 0.4,
                    ease: "easeOut"
                  }}
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.05 }}
                >
                  {item.label}
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;

